<template>
  <DeferredContent @onLoadComponent="fetchLeaderboardTop">
    <div class="flex flex-col gap-2 bg-white rounded-lg shadow overflow-hidden">
      <div
        class="flex justify-between border-b p-2 sm:px-4"
        :class="{
          'border-untitled-gray-200': !kintzApp,
          'bg-kintz-primary-1 text-kintz-primary-2 border-kintz-primary-2':
            kintzApp,
        }"
      >
        <div class="flex justify-start items-center">
          <h1 class="text-lg font-bold">Leaderboard</h1>
        </div>
        <div class="flex justify-center items-center">
          <ElButton
            :icon="EyeIcon"
            size="small"
            :color="kintzApp ? kintzAppColors.primary_4 : ''"
            @click="goToLeaderboard"
          >
            View all
          </ElButton>
        </div>
      </div>

      <div
        class="flex gap-4 px-2 md:px-4 pt-2 border-b border-untitled-gray-100"
        role="tablist"
      >
        <button
          v-for="tab in tabOptions"
          :key="tab.name"
          type="button"
          role="tab"
          :aria-selected="activeTab === tab.name"
          class="pb-2 text-sm font-semibold border-b-2 transition-colors -mb-px"
          :class="
            activeTab === tab.name
              ? kintzApp
                ? 'border-kintz-primary-2 text-kintz-primary-2'
                : 'border-untitled-gray-800 text-untitled-gray-800'
              : 'border-transparent text-untitled-gray-500 hover:text-untitled-gray-700'
          "
          @click="activeTab = tab.name"
        >
          {{ tab.label }}
        </button>
      </div>

      <div
        class="w-full flex flex-col gap-0 max-h-96 overflow-hidden overflow-y-auto pb-2 md:px-2"
      >
        <template v-if="loading">
          <div
            class="w-full min-h-48 flex justify-center items-center px-2 md:px-4"
          >
            <LoadingSpinner />
          </div>
        </template>

        <template v-else-if="currentRows.length">
          <div
            v-for="row in currentRows"
            :key="`${activeTab}-${row.id}-${row.list_divider_before ? 'app' : 'top'}`"
            class="flex flex-col gap-1 border-b border-untitled-gray-100 last:border-0 px-2 md:px-4 py-2.5 transition-colors"
            :class="[
              row.list_divider_before
                ? kintzApp
                  ? 'border-t-2 border-dashed border-kintz-primary-2/35 mt-1'
                  : 'border-t-2 border-dashed border-untitled-gray-300 mt-1'
                : '',
              isRowCurrentUser(row)
                ? kintzApp
                  ? 'bg-kintz-primary-1/70 ring-2 ring-inset ring-kintz-primary-2/40 hover:bg-kintz-primary-1/80'
                  : 'bg-sky-50 ring-2 ring-inset ring-sky-300/80 hover:bg-sky-50'
                : 'hover:bg-untitled-gray-50',
            ]"
          >
            <div class="flex flex-1 justify-between items-start gap-2">
              <div class="flex items-start gap-2 min-w-0 flex-1">
                <span
                  class="shrink-0 inline-flex items-center justify-center h-7 w-7 rounded-full text-xs font-bold"
                  :class="
                    kintzApp
                      ? 'bg-kintz-primary-1 text-kintz-primary-2'
                      : 'bg-untitled-gray-100 text-untitled-gray-700'
                  "
                >
                  {{ row.rank }}
                </span>
                <div class="min-w-0 flex-1">
                  <p
                    class="font-semibold text-sm line-clamp-2 flex flex-wrap items-center gap-1.5"
                    :class="
                      isRowCurrentUser(row)
                        ? kintzApp
                          ? 'text-kintz-primary-2'
                          : 'text-sky-900'
                        : 'text-untitled-gray-600'
                    "
                  >
                    {{ row.name }}
                    <span
                      v-if="isRowCurrentUser(row)"
                      class="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide"
                      :class="
                        kintzApp
                          ? 'bg-white/90 text-kintz-primary-2 border border-kintz-primary-2/50'
                          : 'bg-white text-sky-700 border border-sky-300'
                      "
                    >
                      You
                    </span>
                  </p>
                  <p
                    v-if="showPosition && row.position_name"
                    class="text-xs text-untitled-gray-500 mt-0.5 line-clamp-1"
                  >
                    {{ row.position_name }}
                  </p>
                </div>
              </div>
              <div class="shrink-0 text-right">
                <p
                  class="text-sm font-bold tabular-nums"
                  :class="kintzApp ? 'text-kintz-primary-2' : 'text-untitled-gray-800'"
                >
                  <template v-if="activeTab === 'points'">
                    {{ row.points ?? 0 }}
                  </template>
                  <template v-else>
                    {{ row.badge_count ?? 0 }}
                  </template>
                </p>
                <p class="text-[10px] uppercase tracking-wide text-untitled-gray-400">
                  {{ activeTab === "points" ? "pts" : "badges" }}
                </p>
              </div>
            </div>
          </div>
        </template>

        <template v-else>
          <div
            class="w-full min-h-48 flex text-sm justify-center items-center font-semibold text-untitled-gray-400 px-4"
          >
            No leaderboard data yet.
          </div>
        </template>
      </div>
    </div>
  </DeferredContent>
</template>

<script setup>
import DeferredContent from "@/components/utilities/DeferredContent.vue";
import LoadingSpinner from "@/components/utilities/LoadingSpinner.vue";
import { kintzAppColors } from "@/composable/kintz/kintz-app.color";
import { getRoles } from "@/composable/users/getRoles.js";
import { useAuthStore } from "@/stores";
import { EyeIcon } from "@heroicons/vue/solid";
import { ElButton } from "element-plus";
import { computed, inject, onMounted, onUnmounted, ref } from "vue";
import { useRouter } from "vue-router";
import eventBus from "@/plugins/eventBus";

const kintzApp = inject("kintz-app", false);
const axios = inject("axios");
const router = useRouter();
const authUser = useAuthStore();

const roles = getRoles();

const showPosition = computed(() => {
  return (
    authUser.isSuperAdmin ||
    authUser.isAccountManager ||
    roles.isSpecificManager?.value ||
    roles.isMultiStoreManager?.value
  );
});

const tabOptions = [
  { name: "points", label: "Most points" },
  { name: "badges", label: "Most badges" },
];

const activeTab = ref("points");
const loading = ref(false);
const byPoints = ref([]);
const byBadges = ref([]);
const selectedStoreFromSwitch = ref(null);

const myUserId = computed(() => Number(authUser?.user?.id));

const isRowCurrentUser = (row) => {
  if (row?.is_current_user === true) {
    return true;
  }
  return Number(row?.id) === myUserId.value && Number.isFinite(myUserId.value);
};

const currentRows = computed(() => {
  return activeTab.value === "points" ? byPoints.value : byBadges.value;
});

const loadStoreFilter = () => {
  const storedStore = localStorage.getItem("selected_store");
  if (storedStore) {
    try {
      selectedStoreFromSwitch.value = JSON.parse(storedStore);
    } catch {
      localStorage.removeItem("selected_store");
    }
  }
};

const buildParams = () => {
  const params = {};
  if (selectedStoreFromSwitch.value?.id) {
    params.store_id = selectedStoreFromSwitch.value.id;
  }
  return params;
};

const fetchLeaderboardTop = async () => {
  loadStoreFilter();
  loading.value = true;
  try {
    const { data } = await axios.get("/api/leaderboard/top", {
      params: buildParams(),
    });
    byPoints.value = data?.by_points ?? [];
    byBadges.value = data?.by_badges ?? [];
  } catch {
    byPoints.value = [];
    byBadges.value = [];
  } finally {
    loading.value = false;
  }
};

const handleStoreSwitch = (store) => {
  selectedStoreFromSwitch.value = store;
  fetchLeaderboardTop();
};

const goToLeaderboard = () => {
  router.push({ name: "leaderboard" });
};

onMounted(() => {
  loadStoreFilter();
  eventBus.$on("STORE_SWITCHED", handleStoreSwitch);
});

onUnmounted(() => {
  eventBus.$off("STORE_SWITCHED", handleStoreSwitch);
});
</script>

